import * as React from 'react'
import { ReactElement, useRef } from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import LinearProgress from '@mui/material/LinearProgress'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Paper from '@mui/material/Paper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import IconButton from '@mui/material/IconButton'
import Popper from '@mui/material/Popper'
import { styled } from '@mui/material/styles'

import { useData } from './hooks/useData'
import { useHandlers } from './hooks/useHandlers'
import { BillToSelectChip } from './BillToSelectChip'
import { BillToSearchCard } from '@user/components/Location/BillToSelectScroll/BillToSelectCard/BillToSearchCard/BillToSearchCard'

type Props = {
  branchId?: string
  delayTimeMS?: number
  label?: ReactElement | string
  locationId?: string
  onChange: (locationId: string, locationCode?: string) => void
  onClearClick: () => void
  placeHolderText?: string
  isInValid?: boolean
  error?: string | undefined
}

const StyledPopper = styled(Popper)(() => ({
  zIndex: 1300,
}))

const DropdownPaper = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  maxHeight: 400,
  overflowY: 'auto',
  boxShadow: theme.shadows[4],
}))

export function BillToSearchSelect(props: Props) {
  const { onChange, onClearClick, isInValid, error } = props

  // Use Data Hook
  const {
    selectedBillToId,
    setSelectedBillToId,
    open,
    setOpen,
    allBillTos,
    isFetchingMore,
    setIsFetchingMore,
    loading,
    onChangePage,
    onChangeSearchText,
    onClearSearchText,
    page,
    pages,
    searchText,
  } = useData()

  // Use Handlers Hook
  const {
    handleChangeRadio,
    handleChangeSearchText,
    handleClearSearchText,
    handleScrollSafe,
    handleClickAway,
    handleInputFocus,
  } = useHandlers({
    allBillTos,
    loading,
    isFetchingMore,
    page,
    pages,
    searchText,
    onChange,
    onClearClick,
    onChangePage,
    onChangeSearchText,
    onClearSearchText,
    setSelectedBillToId,
    setIsFetchingMore,
    setOpen,
  })

  // Refs
  const anchorRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Find the selected BillTo object
  const selectedBillTo = React.useMemo(() => {
    return allBillTos.find((b) => b.id === selectedBillToId)
  }, [allBillTos, selectedBillToId])

  const handleClearChip = () => {
    handleClearSearchText()
  }

  const isSelected = Boolean(selectedBillToId && selectedBillTo)

  if (isSelected) {
    return (
      <BillToSelectChip location={selectedBillTo!} onClear={handleClearChip} />
    )
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box position="relative" ref={anchorRef}>
        <TextField
          value={searchText}
          onChange={handleChangeSearchText}
          fullWidth
          required
          spellCheck={false}
          error={isInValid}
          helperText={isInValid ? error : undefined}
          onFocus={handleInputFocus}
          InputProps={{
            inputProps: {
              maxLength: 30,
              'data-test-id': 'billToSearchInput',
            },
            placeholder: 'Bill-To Location *',
            endAdornment: searchText && (
              <IconButton
                sx={{ mr: -1 }}
                onClick={handleClearSearchText}
                data-test-id="billToScrollSearchClearBtn"
              >
                <CloseIcon />
              </IconButton>
            ),
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="medium" />
              </InputAdornment>
            ),
          }}
        />

        {loading && page === 1 && <LinearProgress sx={{ mt: 1 }} />}

        <StyledPopper
          open={Boolean(
            open &&
              (allBillTos.length > 0 ||
                (searchText && !loading && allBillTos.length === 0)),
          )}
          anchorEl={anchorRef.current}
          sx={{ width: anchorRef.current?.offsetWidth }}
          placement="top-start"
        >
          {allBillTos.length > 0 && (
            <DropdownPaper
              onScroll={handleScrollSafe}
              ref={listRef}
              sx={{ backgroundColor: '#FAFAFA' }}
            >
              {loading && page > 1 && <LinearProgress />}
              <Box p="16px">
                {allBillTos.map((item) => (
                  <BillToSearchCard
                    key={item.id}
                    location={item}
                    onClick={handleChangeRadio}
                    searchText={searchText}
                  />
                ))}
                {loading && page < pages && <LinearProgress sx={{ mt: 1 }} />}
              </Box>
            </DropdownPaper>
          )}
          {!loading && searchText && allBillTos.length === 0 && (
            <DropdownPaper>
              <Box p={2}>
                <Typography variant="body2">No Locations Found</Typography>
              </Box>
            </DropdownPaper>
          )}
        </StyledPopper>
      </Box>
    </ClickAwayListener>
  )
}
